import type { z } from 'zod';
import { type TransformerConfig } from '../common/types';
import { createDecodeFromDbFn, createEncodeToDbFn, createTransformer } from '../common/utils';
import { DBPermissionSchema, PermissionSchema } from './schemas';
import { getPermissionTagPartition, readPermissionTag } from './tags';

type Permission = z.infer<typeof PermissionSchema>;
type DBPermission = z.infer<typeof DBPermissionSchema>;

export const PermissionCodecConfig: TransformerConfig<
  typeof PermissionSchema,
  typeof DBPermissionSchema
> = {
  name: 'Permission',
  clientSchema: PermissionSchema,
  dbSchema: DBPermissionSchema,
  transformers: [
    // id ↔ permissionId and its partition; the meaning its tag holds is read, not stored
    createTransformer<typeof PermissionSchema, typeof DBPermissionSchema>({
      encoder: {
        sourceFields: [
          'id',
          'type',
          'subject',
          'categoryCode',
          'topicCode',
          'noteCode',
          'courseId',
        ] as const,
        encode: ({ id }) =>
          id === undefined ? {} : { permissionId: id, partition: getPermissionTagPartition(id) },
      },
      decoder: {
        sourceFields: ['permissionId'] as const,
        decode: ({ permissionId }) => ({ id: permissionId, ...readPermissionTag(permissionId) }),
      },
    }),

    // permissionGroupIds: [groupId] ↔ groups: { groupId: {} }
    createTransformer<typeof PermissionSchema, typeof DBPermissionSchema>({
      encoder: {
        sourceFields: ['permissionGroupIds'] as const,
        encode: ({ permissionGroupIds }) => ({
          groups: Object.fromEntries(
            permissionGroupIds.map((g: Permission['permissionGroupIds'][number]) => [g, {}])
          ),
        }),
      },
      decoder: {
        sourceFields: ['groups'] as const,
        decode: ({ groups = {} }) => ({
          permissionGroupIds: Object.keys(groups),
        }),
      },
    }),

    // streams: [{ course, streamId }] ↔ streams: { "course#streamId": {} }
    createTransformer<typeof PermissionSchema, typeof DBPermissionSchema>({
      encoder: {
        sourceFields: ['streams'] as const,
        encode: ({ streams }) => ({
          streams: Object.fromEntries(
            streams.map((s: Permission['streams'][number]) => [`${s.course}#${s.streamId}`, {}])
          ),
        }),
      },
      decoder: {
        sourceFields: ['streams'] as const,
        decode: ({ streams = {} }) => ({
          streams: Object.keys(streams).map(key => {
            const [course, streamId] = key.split('#');
            return { course: course!, streamId: streamId! };
          }),
        }),
      },
    }),

    // lessonTemplates: [{ course, lessonId }] ↔ lessonTemplates: { "course#lessonId": {} }
    createTransformer<typeof PermissionSchema, typeof DBPermissionSchema>({
      encoder: {
        sourceFields: ['lessonTemplates'] as const,
        encode: ({ lessonTemplates }) => ({
          lessonTemplates: Object.fromEntries(
            lessonTemplates.map((t: Permission['lessonTemplates'][number]) => [
              `${t.course}#${t.lessonId}`,
              {},
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['lessonTemplates'] as const,
        decode: ({ lessonTemplates = {} }) => ({
          lessonTemplates: Object.keys(lessonTemplates).map(key => {
            const [course, lessonId] = key.split('#');
            return { course: course!, lessonId: lessonId! };
          }),
        }),
      },
    }),

    // users: [{ userId, name, type }] ↔ users: { userId: { name, type } }
    createTransformer<typeof PermissionSchema, typeof DBPermissionSchema>({
      encoder: {
        sourceFields: ['users'] as const,
        encode: ({ users }) => ({
          users: Object.fromEntries(
            users.map((u: Permission['users'][number]) => [
              u.userId,
              { name: u.name, type: u.type },
            ])
          ),
        }),
      },
      decoder: {
        sourceFields: ['users'] as const,
        decode: ({ users = {} }) => ({
          users: Object.entries(users).map(([userId, details]) => ({
            userId,
            ...(details as NonNullable<DBPermission['users']>[string]),
          })),
        }),
      },
    }),
  ],
};

export const encodePermissionToDb = createEncodeToDbFn(PermissionCodecConfig);
export const decodePermissionFromDb = createDecodeFromDbFn(PermissionCodecConfig);
